// SEO Configuration for all pages
// Keywords and descriptions based on keyword research

export const seoConfig = {
  // Homepage
  home: {
    title: 'All-in-One Salon Management Software for India',
    description: 'AyeSalon is India\'s all-in-one salon management software. Manage appointments, staff, inventory & billing from one platform. Trusted by 500+ salons. Book a free demo today.',
    canonical: '/',
    ogImage: '/og-home.jpg',
    keywords: 'salon management software india, salon software india, all-in-one salon software, salon crm india, salon and spa management software',
    h1: 'All-in-One Salon Management Software for India',
    h2Targets: [
      'Complete Salon Management Platform',
      'Appointment Scheduling & Client Management',
      'Staff Management & Payroll',
      'Inventory & Billing System',
      'Analytics & Business Insights',
      'Secure & Reliable Cloud Solution',
      'Trusted by 500+ Salons Across India',
      'Book Your Free Demo',
    ],
  },

  // Features page
  features: {
    title: 'Salon Management Software Features',
    description: 'Explore AyeSalon\'s complete feature set — appointment scheduling, staff management, GST billing, client CRM, inventory tracking, loyalty programs and WhatsApp marketing for Indian salons.',
    canonical: '/features',
    ogImage: '/og-features.jpg',
    keywords: 'salon management software features, salon software features list, what does salon software do, ayesalon features',
    h1: 'Salon Management Software Features',
    h2Targets: [
      'Appointment Scheduling Software',
      'Staff Management & Payroll',
      'Client CRM & Management',
      'Inventory Tracking System',
      'Discount & Offers Management',
      'Business Analytics Dashboard',
      'GST-Ready Invoice System',
      'Loyalty & Membership Programs',
    ],
  },

  // Feature detail pages
  appointmentScheduling: {
    title: 'Online Salon Appointment Scheduling Software',
    description: 'Book salon appointments online 24/7 with AyeSalon. Automated SMS & WhatsApp reminders, real-time calendar sync, no-show protection for salons and spas in India.',
    canonical: '/features/appointment-scheduling',
    ogImage: '/og-features.jpg',
    keywords: 'salon appointment scheduling software, online salon booking system india, salon booking software, salon scheduling software, automated salon booking, reduce salon no-shows',
    h1: 'Online Salon Appointment Scheduling Software',
    h2Targets: ['24/7 Online Booking System', 'Automated Reminders', 'Real-Time Calendar Sync', 'No-Show Protection', 'Staff Assignment'],
  },

  staffManagement: {
    title: 'Salon Staff Management Software India',
    description: 'Onboard staff, build shift schedules, and track performance and certifications in one place. Role-based access for Indian salons — see AyeSalon\'s dedicated attendance & payroll module for clock-in and payslips.',
    canonical: '/features/staff-management',
    ogImage: '/og-features.jpg',
    keywords: 'salon staff management software, salon employee management, salon staff scheduling software, salon commission tracking software, salon staff onboarding software',
    h1: 'Salon Staff Management Software',
    h2Targets: ['Staff Scheduling', 'Employee Onboarding', 'Commission Structure Setup', 'Performance Analytics', 'Role-Based Access'],
  },

  clientsManagement: {
    title: 'Salon Client Management Software & CRM',
    description: 'Centralised client profiles, service history, preferences and loyalty programs. Build stronger client relationships and boost retention with AyeSalon CRM for Indian salons.',
    canonical: '/features/clients-management',
    ogImage: '/og-features.jpg',
    keywords: 'salon client management software, salon crm india, salon customer management system, salon loyalty program software india, client retention salon software',
    h1: 'Salon Client Management Software & CRM',
    h2Targets: ['Complete Client Profiles', 'Service History Tracking', 'Client Preferences', 'Communication History', 'Loyalty Programs'],
  },

  inventoryManagement: {
    title: 'Salon Inventory Management Software India',
    description: 'Track salon product stock levels, set low-inventory alerts and manage suppliers. Never run out of supplies mid-service. Book a free demo for Indian salons.',
    canonical: '/features/inventory-management',
    ogImage: '/og-features.jpg',
    keywords: 'salon inventory management software, salon stock management software india, salon product tracking software, salon inventory control, salon supply management',
    h1: 'Salon Inventory Management Software',
    h2Targets: ['Real-Time Stock Tracking', 'Low Inventory Alerts', 'Product Pricing', 'Supplier Management', 'Purchase Orders'],
  },

  discountOffers: {
    title: 'Salon Discount & Offers Management Software',
    description: 'Create targeted discounts, flash sales, gift cards and loyalty offers for salon clients. Boost repeat visits and revenue with AyeSalon\'s promotions engine for Indian salons.',
    canonical: '/features/discount-offers',
    ogImage: '/og-features.jpg',
    keywords: 'salon discount management software, salon offers and promotions software, salon loyalty offers india, salon gift card software india, salon coupon management',
    h1: 'Salon Discount & Offers Management',
    h2Targets: ['Create Discounts', 'Flash Sales & Offers', 'Gift Card Management', 'Referral Rewards', 'Promotional Campaigns'],
  },

  clientAnalytics: {
    title: 'Salon Reports & Business Analytics Software',
    description: 'Auto-generated daily, weekly and monthly sales, staff and GST reports, plus revenue and client retention analytics. Data-driven reporting dashboard for Indian salons.',
    canonical: '/features/client-analytics',
    ogImage: '/og-features.jpg',
    keywords: 'salon daily sales report software, salon business reports app, salon reporting software india, salon analytics software, salon revenue analytics, salon performance dashboard, salon KPI tracking software, salon business intelligence',
    h1: 'Salon Reports & Business Analytics Software',
    h2Targets: ['Automated Sales Reports', 'Revenue Analytics', 'Appointment Reports', 'Staff Performance', 'Client Retention Metrics', 'Business Insights'],
  },

  invoiceSystem: {
    title: 'Salon GST Billing & Invoice Software India',
    description: 'GST-ready salon billing, digital invoices, split payment across cash/UPI/card/wallet, WhatsApp receipt sharing. The complete salon POS and invoice system for Indian salons.',
    canonical: '/features/invoice-system',
    ogImage: '/og-features.jpg',
    keywords: 'salon billing software india, salon gst billing software, salon pos software india, salon invoice software, salon upi payment integration, salon bill sharing whatsapp, split bill payment salon software, multiple payment modes salon billing',
    h1: 'Salon Billing & GST Invoice Software',
    h2Targets: ['GST-Ready Invoices', 'Multiple Payment Methods', 'Split Payment Support', 'Digital Receipts', 'Payment History', 'Financial Reports'],
  },

  membershipManagement: {
    title: 'Salon Membership & Loyalty Program Software',
    description: 'Create salon membership plans, track loyalty points and reward repeat clients. Increase retention and build predictable recurring revenue with AyeSalon for Indian salons.',
    canonical: '/features/membership-management',
    ogImage: '/og-features.jpg',
    keywords: 'salon membership management software, salon loyalty program software india, salon membership plans software, salon loyalty points software, salon subscription management',
    h1: 'Salon Membership & Loyalty Program Software',
    h2Targets: ['Membership Plans', 'Loyalty Points System', 'Subscription Billing', 'Member Benefits', 'Retention Analytics'],
  },

  gstAccounting: {
    title: 'GST Accounting Software for Salons India',
    description: 'Automatic GST calculation, CGST/SGST split, GSTIN-compliant invoices, and GSTR-3B ready reports for your salon. AyeSalon keeps Indian salons tax-compliant without manual work.',
    canonical: '/features/gst-accounting',
    ogImage: '/og-features.jpg',
    keywords: 'gst accounting software for salon, salon gst billing software india, gst invoice software for salon, cgst sgst calculation software salon, gstr-3b report salon, salon tax compliance software india',
    h1: 'GST Accounting Software for Indian Salons',
    h2Targets: ['Automatic GST Calculation', 'CGST & SGST Split', 'GSTIN-Ready Invoices', 'GSTR-3B Reports', 'HSN/SAC Code Management'],
  },

  loyaltyRewards: {
    title: 'Salon Loyalty & Rewards Program Software',
    description: 'Turn every visit into loyalty points clients can redeem for rewards. Automated points accrual, tiered rewards, birthday bonuses, and referral points for Indian salons.',
    canonical: '/features/loyalty-rewards',
    ogImage: '/og-features.jpg',
    keywords: 'salon loyalty program software, salon rewards program software india, loyalty points software for salon, salon referral rewards program, salon customer loyalty app, points based rewards system salon',
    h1: 'Salon Loyalty & Rewards Program Software',
    h2Targets: ['Points-Based Rewards', 'Tiered Loyalty Levels', 'Birthday & Anniversary Rewards', 'Referral Points', 'Redemption Tracking'],
  },

  multiBranchManagement: {
    title: 'Multi-Branch Salon Management Software India',
    description: 'Manage unlimited salon branches from one dashboard. Centralized staff, inventory, and reporting with branch-level control for Indian salon chains.',
    canonical: '/features/multi-branch-management',
    ogImage: '/og-features.jpg',
    keywords: 'multi-branch salon software, salon chain management software india, multi-location salon software, centralized salon management software, multi-branch inventory management salon, manage multiple salon locations software',
    h1: 'Multi-Branch Salon Management Software',
    h2Targets: ['Centralized Multi-Branch Dashboard', 'Branch-Level Permissions', 'Cross-Branch Inventory', 'Consolidated Reporting', 'Unified Client Database'],
  },

  staffAttendancePayroll: {
    title: 'Salon Staff Attendance & Payroll Software India',
    description: 'Mobile check-in/check-out, automatic overtime calculation, and commission-based payroll with instant payslips. Replace manual attendance registers and Excel payroll for Indian salons.',
    canonical: '/features/staff-attendance-payroll',
    ogImage: '/og-features.jpg',
    keywords: 'salon staff attendance app, salon payroll software india, salon staff commission calculator, salon employee attendance tracking software, salon payslip generator software, salon overtime calculation software',
    h1: 'Salon Staff Attendance & Payroll Software',
    h2Targets: ['Mobile Attendance Tracking', 'Automatic Overtime Calculation', 'Commission-Based Payroll', 'Payslip Generation', 'Compliance Audit Trail'],
  },

  expenseManagement: {
    title: 'Salon Expense Management Software India',
    description: 'Track rent, supplies, utilities, and staff advances by category and branch. See real salon profit, not just revenue, with AyeSalon expense tracking for Indian salons.',
    canonical: '/features/expense-management',
    ogImage: '/og-features.jpg',
    keywords: 'salon expense tracking software, salon expense management app india, track salon supplier bills, salon overhead tracking software, salon profit and loss software india, salon budget tracking app',
    h1: 'Salon Expense Management Software',
    h2Targets: ['Categorized Expense Tracking', 'Branch-Wise Expense Reports', 'Supplier Bill Tracking', 'Recurring Expense Reminders', 'Real Profit Visibility'],
  },

  // Other pages
  pricing: {
    title: 'Salon Management Software Pricing & Plans',
    description: 'Flexible pricing for salons of all sizes. Book a free personalized demo, no credit card required. Pay only for features you use. Plans from single-chair studios to multi-location chains.',
    canonical: '/pricing',
    ogImage: '/og-pricing.jpg',
    keywords: 'salon management software pricing, salon software cost india, free salon management software india, book salon software demo, affordable salon software india, salon software for small business india',
  },

  about: {
    title: 'About AyeSalon — Our Mission & Values',
    description: 'Learn about AyeSalon\'s mission to empower Indian salons with technology. Built by Kautix AI Solutions LLP. Trusted by 500+ salons across India since 2025.',
    canonical: '/about',
    ogImage: '/og-about.jpg',
    keywords: 'salon software company india, salon technology company india, about ayesalon, kautix ai solutions, beauty wellness saas india',
  },

  contact: {
    title: 'Contact AyeSalon — Get Support',
    description: 'Have questions about AyeSalon salon management software? Contact our team. We respond within 2 hours. Call +91-7090238960 or email support@ayesalon.com',
    canonical: '/contact',
    ogImage: '/og-home.jpg',
    keywords: 'contact ayesalon, salon software support, ayesalon support team',
  },

  privacyPolicy: {
    title: 'Privacy Policy — How AyeSalon Protects Your Data',
    description: 'AyeSalon privacy policy explaining how we collect, use and protect your data.',
    canonical: '/privacyPolicy',
    ogImage: '/og-home.jpg',
    keywords: 'privacy policy, data protection',
  },

  // Comparison Pages (Phase 5)
  'comparison-vs-excel': {
    title: 'AyeSalon vs. Excel: Which Salon Software is Better?',
    description: 'Compare AyeSalon salon management software with Excel spreadsheets. See why 500+ Indian salons switched and save ₹64,000/month in no-shows.',
    canonical: '/comparison/vs-excel',
    ogImage: '/og-home.jpg',
    keywords: 'salon management software vs excel, salon software comparison, AyeSalon alternative, best salon management system India',
    h1: 'AyeSalon vs. Excel: Which is Better for Your Salon?',
    h2Targets: [
      'Feature-by-Feature Comparison',
      'Cost Analysis: Excel vs. AyeSalon',
      'Time Savings: 10+ Hours Per Week',
      'Real-World Example: Shine Beauty, Mumbai',
      'When to Use Each',
      'Key Benefits of AyeSalon',
    ],
  },

  'comparison-vs-paper': {
    title: 'AyeSalon vs. Paper-Based Systems: Why Salons Are Going Digital',
    description: 'Stop losing money to no-shows and manual admin work. Compare paper booking with AyeSalon digital salon management. Save 40+ hours/month and recover ₹30,000+ in revenue.',
    canonical: '/comparison/vs-paper',
    ogImage: '/og-home.jpg',
    keywords: 'paper-based salon booking, manual appointment booking system, salon software vs paper, digital salon management, salon booking system, salon software india',
    h1: 'AyeSalon vs. Paper: Digital Revolution for Indian Salons',
    h2Targets: [
      'The Real-World Impact',
      'Feature-by-Feature Comparison',
      'Paper vs Digital: Side-by-Side',
      'Time Savings Breakdown',
      'Why Paper-Based Systems Fail',
      'How AyeSalon Fixes Everything',
      'Real Example: From Paper to Digital',
    ],
  },

  // Success Stories (Phase 5)
  'success-story': {
    title: 'Shine Beauty: ₹64,000/Month Revenue Growth',
    description: 'Real success story from Shine Beauty, Mumbai. How they reduced no-shows by 80% and increased revenue by ₹64,000/month using AyeSalon salon management software.',
    canonical: '/success-story/shine-beauty',
    ogImage: '/og-home.jpg',
    keywords: 'salon success story, salon revenue increase, AyeSalon case study, salon software ROI, salon business growth India',
    h1: 'How Shine Beauty Increased Revenue by ₹64,000/Month',
    h2Targets: [
      'The Impact: Before & After Metrics',
      'The Challenge: Excel + Paper Systems',
      'The Solution: AyeSalon',
      'Implementation Timeline',
      'The Results After 4 Weeks',
      'Financial Impact & ROI',
      'Operational Impact',
      'Key Learnings',
    ],
  },

  // How-To Guides (Phase 5 Week 2)
  'guide-setup-5step': {
    title: '5-Step AyeSalon Setup Guide: Go Live in 30 Minutes',
    description: 'Step-by-step guide to set up AyeSalon salon management software in under 30 minutes. No coding required. Perfect for first-time users.',
    canonical: '/guides/setup-5-step',
    keywords: 'ayesalon setup guide, how to set up salon software, salon booking setup, salon management software tutorial',
  },

  'guide-reduce-no-shows': {
    title: 'Reduce No-Shows by Up to 40%: Complete Strategy Guide',
    description: '5-tactic strategy to cut salon no-shows by up to 40% using AyeSalon, recovering an estimated ₹10,000-₹15,000/month in lost revenue.',
    canonical: '/guides/reduce-no-shows',
    keywords: 'reduce salon no-shows, no-show strategy, appointment confirmations, salon reminders, salon no-show prevention',
  },

  'guide-save-40-hours': {
    title: 'Save 40 Hours Per Month: Complete Automation Blueprint',
    description: 'Automate all salon admin tasks. From 50+ hours/month to 10 hours. Save ₹16,000/month in labor costs. Real breakdown with numbers.',
    canonical: '/guides/save-40-hours',
    keywords: 'salon admin automation, save time salon, automated scheduling, salon staff management, administrative automation',
  },

  'guide-commission': {
    title: 'Staff Commission System: Automate Payroll & Eliminate Disputes',
    description: 'Complete guide to implementing fair staff commission system in AyeSalon. Automatic calculation, zero disputes, full transparency. 3 proven commission models.',
    canonical: '/guides/commission-system',
    keywords: 'salon staff commission, commission calculation, salon payroll, staff payment system, commission management',
  },

  'guide-mobile-booking': {
    title: 'Mobile Booking Optimization: Get 50% More Online Bookings',
    description: 'Complete guide to optimizing your salon\'s mobile booking experience. Share booking link on WhatsApp, Instagram, SMS. Get 50% more online bookings from your phone.',
    canonical: '/guides/mobile-booking',
    keywords: 'mobile booking optimization, salon booking link, whatsapp booking, online salon booking, client booking experience',
  },

  'guide-inventory': {
    title: 'Inventory Tracking for Salons: Never Run Out of Stock',
    description: 'Complete guide to tracking salon inventory in AyeSalon. Set low-stock alerts. Track product usage. Know exactly what you need to order.',
    canonical: '/guides/inventory-tracking',
    keywords: 'salon inventory management, product tracking, stock management, inventory alerts, salon stock control',
  },

  'guide-whatsapp': {
    title: 'WhatsApp Marketing for Salons: Free Reach to 100% of Clients',
    description: 'Use WhatsApp Business to send bulk offers, promotions, and booking reminders. Free. Unlimited reach. 80% open rate. Complete setup guide.',
    canonical: '/guides/whatsapp-marketing',
    keywords: 'whatsapp business marketing, salon whatsapp, whatsapp marketing strategy, salon promotions, client engagement',
  },

  'guide-pricing': {
    title: 'Salon Pricing Strategy: Increase Revenue by 20-30%',
    description: 'Complete guide to salon pricing strategy. Dynamic pricing, seasonal discounts, VIP pricing. Increase revenue 20-30% without losing clients.',
    canonical: '/guides/pricing-strategy',
    keywords: 'salon pricing strategy, service pricing, dynamic pricing, salon revenue optimization, pricing psychology',
  },

  // Guides Hub
  'guides-hub': {
    title: 'AyeSalon Guides — How-To Tutorials & Strategy Guides',
    description: 'Complete how-to guides for salon owners. Setup, reduce no-shows, save time, optimize pricing, grow revenue. Real numbers. Step-by-step instructions.',
    canonical: '/guides',
    ogImage: '/og-home.jpg',
    keywords: 'salon guides, salon management tutorials, salon how-to, salon strategy, salon optimization, salon setup guide',
    h1: 'AyeSalon Guides',
    h2Targets: [
      'Getting Started',
      'Revenue & Growth',
      'Staff Management',
      'Client Engagement',
      'Operations',
    ],
  },

  // Phase 5 Week 3 Content Pages
  testimonials: {
    title: 'AyeSalon Customer Testimonials & Success Stories',
    description: 'Read 12 real success stories from salon owners using AyeSalon. See how they increased revenue, reduced no-shows, and streamlined operations.',
    canonical: '/testimonials',
    ogImage: '/og-home.jpg',
    keywords: 'salon success stories, salon customer testimonials, salon software reviews, ayesalon reviews, salon business growth',
    h1: 'Customer Success Stories',
    h2Targets: [
      'Real Salon Owner Stories',
      'Revenue Growth Results',
      'No-Show Reduction Success',
      'Staff Management Impact',
      'Time & Cost Savings',
      'Verified Customer Testimonials',
    ],
  },

  'mobile-app': {
    title: 'AyeSalon Partner App — Salon Management on Android & iPhone',
    description: 'Manage multiple salon branches, staff attendance and appointment assignment from your phone. Instant push alerts for stylists and owners. Free on Google Play and the App Store.',
    canonical: '/mobile-app',
    ogImage: '/og-features.jpg',
    keywords: 'salon management app, salon owner app india, salon staff attendance app, multi branch salon app, salon appointment app for staff, ayesalon partner app',
    h1: 'Run every branch of your salon from your phone',
    h2Targets: [
      'Multi-Branch Management',
      'Staff Attendance',
      'Appointment Assignment Notifications',
      'Push Notifications',
    ],
  },

  'roi-calculator': {
    title: 'AyeSalon ROI Calculator — Calculate Your Savings',
    description: 'Calculate how much you can save with AyeSalon. Find your ROI based on staff size, revenue, no-shows, and admin time. Free interactive calculator.',
    canonical: '/roi-calculator',
    ogImage: '/og-home.jpg',
    keywords: 'salon roi calculator, salon software savings, calculate salon savings, salon profit calculator, ayesalon roi',
    h1: 'Calculate Your ROI',
    h2Targets: [
      'No-Show Revenue Recovery',
      'Admin Time Savings',
      'Staff Retention Benefits',
      'Monthly Savings Breakdown',
      'Yearly ROI Calculation',
      'Interactive Savings Calculator',
    ],
  },

  'use-cases': {
    title: 'AyeSalon Use Cases by Salon Type — Setup Guides',
    description: 'Customized setup guides for different salon types: solo stylists, small teams, medium salons, multi-location chains, and spa centers. Real setup scenarios.',
    canonical: '/use-cases',
    ogImage: '/og-home.jpg',
    keywords: 'salon setup guide by type, solo stylist software, team salon software, multi-location salon software, spa management software, salon use cases',
    h1: 'Use Cases by Salon Type',
    h2Targets: [
      'Solo Stylist Setup',
      'Small Team (3-5 Staff)',
      'Medium Salon (10+ Staff)',
      'Multi-Location Chains',
      'Spa & Wellness Centers',
      'Customized Implementation',
    ],
  },

  blog: {
    title: 'Salon Management Blog — Tips, Guides & Strategies',
    description: 'Read expert guides on salon management, marketing, billing, staff management, and growth. Learn how to grow your salon business with AyeSalon insights.',
    canonical: '/blog',
    ogImage: '/og-home.jpg',
    keywords: 'salon management blog, salon tips, salon marketing, salon software guides, how to run a salon, salon business growth',
    h1: 'Salon Management Blog',
    h2Targets: [
      'How-To Guides for Salon Owners',
      'Salon Marketing Strategies',
      'Industry Best Practices',
      'Revenue Growth Tips',
      'Expert Salon Insights',
      'Salon Success Stories',
    ],
  },

  // Use-Case Detail Pages
  'use-cases-solo-stylist': {
    title: 'Solo Stylist Salon Software — Stop Managing Bookings on WhatsApp',
    description: 'AyeSalon helps solo stylists replace WhatsApp chaos with a professional booking system. Automated reminders, client CRM, and GST billing — set up in under 30 minutes. Free demo.',
    canonical: '/use-cases/solo-stylist',
    ogImage: '/og-home.jpg',
    keywords: 'salon software solo stylist india, freelance stylist booking system, independent stylist software india, single chair salon software',
  },

  'use-cases-small-team': {
    title: 'Salon Software for Small Teams (3–5 Staff) — Coordinate & Grow',
    description: 'Manage a 3–5 person salon team with AyeSalon. Staff scheduling, commission tracking, shared booking calendar, and GST billing built for growing Indian salons.',
    canonical: '/use-cases/small-team',
    ogImage: '/og-home.jpg',
    keywords: 'small salon team software india, 3-5 staff salon management, small salon management software india, beauty salon team software',
  },

  'use-cases-medium-salon': {
    title: 'Salon Management Software for Medium Salons (10+ Staff)',
    description: 'Structured staff management, KPI dashboards, inventory control, and multi-service booking for medium-sized Indian salons with 10–30 employees. Free personalized demo.',
    canonical: '/use-cases/medium-salon',
    ogImage: '/og-home.jpg',
    keywords: 'medium salon management software india, salon software 10 staff, salon management system india, salon erp india',
  },

  'use-cases-multi-location': {
    title: 'Multi-Location Salon Management Software India',
    description: 'Manage all your salon branches from one dashboard. Centralised staff, inventory, appointments, and analytics across locations. Built for Indian salon chains.',
    canonical: '/use-cases/multi-location',
    ogImage: '/og-home.jpg',
    keywords: 'multi-location salon software india, salon chain management software, multi-branch salon management india, salon franchise software india',
  },

  'use-cases-spa-wellness': {
    title: 'Spa & Wellness Centre Management Software India',
    description: 'AyeSalon for spas and wellness centres. Manage therapists, treatment rooms, packages, memberships, and client profiles — fully GST-compliant for Indian wellness businesses.',
    canonical: '/use-cases/spa-wellness',
    ogImage: '/og-home.jpg',
    keywords: 'spa management software india, wellness centre software india, spa booking system india, spa crm software india, wellness studio management',
  },

  // Video & Media Pages
  videos: {
    title: 'AyeSalon Customer Video Testimonials — Real Salon Owner Stories',
    description: 'Watch real Indian salon owners share how AyeSalon reduced no-shows, increased revenue, and saved 15+ hours per week. Video testimonials from 500+ customers.',
    canonical: '/videos',
    ogImage: '/og-home.jpg',
    keywords: 'ayesalon video testimonials, salon software reviews india, salon owner success stories, salon software customer reviews',
  },

  'feature-videos': {
    title: 'AyeSalon Feature Demo Videos — See Every Feature in Action',
    description: 'Short demo videos for every AyeSalon feature. Watch how appointment scheduling, staff management, GST billing, and client CRM work in practice for Indian salons.',
    canonical: '/feature-videos',
    ogImage: '/og-features.jpg',
    keywords: 'ayesalon demo video, salon software demo india, salon management software walkthrough, ayesalon features video',
  },
};

export default seoConfig;
